import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dispatchService } from '../services/dispatchService';
import { DispatchFilters } from '../types';

// Custom hooks
export const useDispatches = (filters?: DispatchFilters) => {
  return useQuery({
    queryKey: ['dispatches', filters],
    queryFn: () => dispatchService.getDispatches(filters),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

export const useCreateDispatchByGateEntry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: dispatchService.createByGateEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch'] });
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
    },
  });
};

export const useDispatch = (dispatchId: number | null) => {
  return useQuery({
    queryKey: ['dispatch', dispatchId],
    queryFn: () => dispatchService.getDispatch(dispatchId!),
    enabled: !!dispatchId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

export const useDispatchItems = (dispatchId: number | null) => {
  return useQuery({
    queryKey: ['dispatch', dispatchId, 'items'],
    queryFn: () => dispatchService.getDispatchItems(dispatchId!),
    enabled: !!dispatchId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

export const useAddDispatchItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ dispatchId, payload }: { dispatchId: number; payload: any }) => 
      dispatchService.addDispatchItem(dispatchId, payload),
    onSuccess: (_, { dispatchId }) => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', dispatchId, 'items'] });
      queryClient.invalidateQueries({ queryKey: ['dispatch', dispatchId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
    },
  });
};

export const useConfirmDispatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ dispatchId, remarks, destination, lrNumber, eWaybillNumber, vehicleNo, driverName, driverPhone, transporterName, lrDate, handlingType }: { dispatchId: number; remarks?: string; destination?: string; lrNumber?: string; eWaybillNumber?: string; vehicleNo?: string; driverName?: string; driverPhone?: string; transporterName?: string; lrDate?: string; handlingType?: 'manual' | 'mhe' }) =>
      dispatchService.confirmDispatch(dispatchId, { remarks, destination, lrNumber, eWaybillNumber, vehicleNo, driverName, driverPhone, transporterName, lrDate, handlingType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
    },
  });
};

export const useUpdateDispatchItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dispatchId, itemId, payload }: { dispatchId: number; itemId: number; payload: any }) =>
      dispatchService.updateDispatchItem(dispatchId, itemId, payload),
    onSuccess: (_, { dispatchId }) => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', dispatchId, 'items'] });
      queryClient.invalidateQueries({ queryKey: ['dispatch', dispatchId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
    },
  });
};

export const useDeleteDispatchItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ dispatchId, itemId }: { dispatchId: number; itemId: number }) => 
      dispatchService.deleteDispatchItem(dispatchId, itemId),
    onSuccess: (_, { dispatchId }) => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', dispatchId, 'items'] });
      queryClient.invalidateQueries({ queryKey: ['dispatch', dispatchId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
    },
  });
};


export const useApproveDispatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: dispatchService.approveDispatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch'] });
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
    },
  });
};

export const useRejectDispatch = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: dispatchService.rejectDispatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch'] });
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
    },
  });
};
